// apps/client/src/routes/guards/RequireGrowthPropose.tsx
//
// Route gate for /leads/growth. Two checks, both required:
//   1. `featureFlags.growth === 'propose'`, the same predicate as the Lead
//      Capture top nav (config/leadTabAccess isGrowthProposeEnabled). AppLayout
//      renders its Outlet only after company data loads, so a missing flag
//      here means off, not "still loading" (the RequireEdmEnabled pattern).
//   2. The `lead_capture` add-on, asked of the server (checkFeatureEntitlement)
//      so a revoked add-on closes the page even while the flag stays set. The
//      server is asked only once the flag is on (no RPC otherwise).
// The Growth callables re-check the flag, the add-on and the subscription.

import React from 'react';
import { useOutletContext, Navigate, Outlet } from 'react-router-dom';
import { isGrowthProposeEnabled } from '../../config/leadTabAccess';
import { useFeatureEntitlement } from '../../hooks/useFeatureEntitlement';
import { SkeletonTable } from '../../components/common/SkeletonLoader';

interface AppOutletContext {
  companyData?: { id?: string; companyId?: string; featureFlags?: Record<string, unknown> | null } | null;
}

const RequireGrowthPropose: React.FC = () => {
  const outletContext = useOutletContext<AppOutletContext>();
  const companyData = outletContext?.companyData;
  const flagOn = isGrowthProposeEnabled(companyData?.featureFlags);
  const { hasFeature, loading } = useFeatureEntitlement(
    flagOn ? companyData?.id || companyData?.companyId || undefined : undefined,
    'lead_capture',
  );

  if (!flagOn) return <Navigate to="/leads/inbox" replace />;
  if (loading) return <SkeletonTable rows={4} columns={4} />;
  if (!hasFeature) return <Navigate to="/leads/inbox" replace />;
  return <Outlet context={outletContext} />;
};

export default RequireGrowthPropose;
